// Copyright (c) 2021, 37.78 Tecnologia Ltda. All rights reserved.

import { useEffect, useState } from "react";

import { useLazyQuery } from "@apollo/client";
import { intersection, prop } from "ramda";

import { parse as patientParse } from "../../domain/patient/patient";
import { noop } from "../../util";
import { useTheme } from "../theme";
import { maybeUnknownState } from "./maybeUnknownState";
import patientDataQuery from "./query";

const urls = [process.env.REACT_APP_TOS_URL, process.env.REACT_APP_PRIVACY_URL];

const getHealthStatusState = (data) =>
  data._healthstatus.length > 0
    ? maybeUnknownState.TRUE
    : maybeUnknownState.FALSE;

const hasCurrentConsents = (data) => {
  const consentAgreements = data._consent_policy.map(
    prop("consent_policy_uri")
  );

  return intersection(urls, consentAgreements).length === urls.length;
};

export const usePatient = ({ registered }) => {
  const [patientData, setPatientData] = useState(null);
  const [hasHealthStatus, setHasHealthStatus] = useState(
    maybeUnknownState.UNKNOWN
  );
  const [getPatientData, { loading, data, refetch }] =
    useLazyQuery(patientDataQuery);
  const [patientConsent, setPatientConsent] = useState(
    maybeUnknownState.UNKNOWN
  );
  const [called, setCalled] = useState(false);
  const [onPatientDataLoadCallback, setOnPatientDataLoadCallback] = useState(
    () => noop
  );
  const { setTheme, themes } = useTheme();

  const onPatientDataLoad = (callback) => {
    return setOnPatientDataLoadCallback(() => callback);
  };

  useEffect(() => {
    if (registered && !called) {
      getPatientData();
      setCalled(true);
    }
    if (!loading && data) {
      const patient = patientParse(data.patient[0]);
      setPatientData(patient);
      setTheme(themes[patient.organizationId] || themes.default);
      setHasHealthStatus(getHealthStatusState(data));
      setPatientConsent(hasCurrentConsents(data));
      onPatientDataLoadCallback();
    }
  }, [
    called,
    data,
    getPatientData,
    loading,
    onPatientDataLoadCallback,
    refetch,
    registered,
    setTheme,
    themes,
  ]);

  return {
    hasHealthStatus,
    loading,
    onPatientDataLoad,
    patientConsent,
    patientData,
    refetch,
    setCalled,
  };
};
